import Head from 'next/head';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Hero from '../../components/Hero';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';

const SERVICES = [
  'complete-home-remodeling',
  'kitchen-remodeling',
  'bathroom-remodeling',
  'adu',
  'exterior-painting',
  'earthquake-retrofit',
  'foundations',
  'pavers',
  'outdoor-living',
  'gutter',
  'energy-efficient-windows',
  'solar-panels',
  'artificial-turf',
  'hvac',
  'asphalt-shingles',
  'tile-roof',
  'flat-roof',
  'metal-roof',
  'tpo-roof',
];

const format = (str) =>
  str.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function SitemapServices() {
  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}/sitemap/services`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Sitemap",
        "item": `${baseUrl}/sitemap`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Services",
        "item": pageUrl
      }
    ]
  };

  // OPTIONAL but SAFE (small list only)
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Service Areas by Service",
    "itemListElement": SERVICES.map((service, i) => ({
      "@type": "SiteNavigationElement",
      "position": i + 1,
      "name": format(service),
      "url": `${baseUrl}/services/${service}`
    }))
  };

  return (
    <>
      <Head>
        <title>Service Areas by Service | Modern Build</title>
        <meta name="robots" content="index, follow" />
        <meta name="title" content="Service Areas by Service | Modern Build" />
        <meta
          name="description"
          content="Browse all Modern Build service areas organized by service. Explore kitchen remodeling, bathroom remodeling, roofing, HVAC, and more by location."
        />

        <link rel="canonical" href={pageUrl} />

        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Open Graph */}
        <meta property="og:title" content="Service Areas by Service | Modern Build" />
        <meta
          property="og:description"
          content="Explore all service areas by service type including remodeling, roofing, HVAC, and more across California."
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp"
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Service Areas by Service | Modern Build" />
        <meta
          name="twitter:description"
          content="Browse all service areas by service type including kitchen remodeling, roofing, HVAC, and more."
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Service Areas by Service | Modern Build',
                description:
                  'Browse all Modern Build service areas organized by service type including remodeling, roofing, HVAC, and more.',
                slug: '/sitemap/services'
              })
            )
          }}
        />

        {/* Breadcrumb */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema)
          }}
        />

        {/* OPTIONAL ItemList (safe size) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(itemListSchema)
          }}
        />
      </Head>

      <Header />
      <Hero category="sitemap" page="services" hasContentH1={true} />

      <main className="container mx-auto px-4 py-10 max-w-6xl">

        <h1 className="text-3xl font-gilda mb-4">
          Service Areas by Service
        </h1>

        <p className="mb-6 text-gray-600">
          Select a service below to explore all cities and locations where Modern Build provides expert remodeling and construction services.
        </p>

        <ul className="grid md:grid-cols-3 gap-3">
          {SERVICES.map((service) => (
            <li key={service}>
              <a
                href={`/sitemap/services/${service}`}
                className="text-primary hover:underline"
              >
                {format(service)}
              </a>
            </li>
          ))}
        </ul>

      </main>

      <Footer />
    </>
  );
}